import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { images, whyChoose } from '../data/mockData';
import {
  Waves,
  ArrowRight,
  Droplet,
  Zap,
  Shield,
  Sparkles,
  Heart,
  CheckCircle,
  Star,
  Users,
  Play,
  ExternalLink,
  Thermometer,
} from 'lucide-react';

const iconMap = {
  droplet: Droplet,
  zap: Zap,
  shield: Shield,
  sparkles: Sparkles,
  heart: Heart,
  'check-circle': CheckCircle,
};

const HERO_VIDEO_URL = 'https://customer-assets.emergentagent.com/job_eb4b9bb9-81a9-4a45-a21f-b2856d0d9f8b/artifacts/fndjzhv7_hurupu%20pools%20homepage.mp4';

const HomePage = ({ onVideoReady }) => {
  const videoRef = React.useRef(null);
  const hasFired = React.useRef(false);

  const handleVideoReady = () => {
    if (!hasFired.current && onVideoReady) {
      hasFired.current = true;
      onVideoReady();
    }
  };

  const stats = [
    { number: '25m', label: 'Pool Length' },
    { number: '0%', label: 'Chlorine' },
    { number: '500+', label: 'Happy Members' },
    { number: '6+', label: 'Training Programs' },
  ];

  return (
    <div className="min-h-screen">
      {/* ========== HERO SECTION - DESKTOP: video bg / MOBILE: video top + text below ========== */}
      <section data-testid="hero-section">
        {/* ----- DESKTOP HERO (md+) ----- */}
        <div className="hidden md:flex relative min-h-[90vh] items-end overflow-hidden">
          <div className="absolute inset-0 z-0">
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover bg-black"
              onCanPlayThrough={handleVideoReady}
              onPlaying={handleVideoReady}
            >
              <source src={HERO_VIDEO_URL} type="video/mp4" />
            </video>
          </div>
          <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black/75 via-black/25 to-black/10" />

          {/* Heated Pool Badge - Top Right */}
          <div className="absolute top-20 right-6 z-[2]" data-testid="heated-pool-badge">
            <div className="flex items-center gap-1.5 bg-cyan-600/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-full shadow-md text-xs font-medium">
              <Thermometer className="h-3.5 w-3.5" />
              <span>Heated Pool</span>
            </div>
          </div>

          <div className="relative z-[2] w-full pb-12 pt-40 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="max-w-2xl space-y-4">
                <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-400/30 backdrop-blur-sm text-xs px-3 py-0.5">
                  Bangalore's Only Non-Chlorine Pool
                </Badge>
                <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight">
                  Swim Pure.{' '}
                  <span className="bg-gradient-to-r from-cyan-400 to-teal-300 bg-clip-text text-transparent">
                    Swim Safe.
                  </span>
                </h1>
                <p className="text-base text-gray-300 leading-relaxed max-w-xl">
                  Experience India's most advanced non-chlorine swimming pool powered by salt ionization
                  and UV filtration technology.
                </p>
                <div className="flex flex-row gap-3 pt-1">
                  <Button asChild size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-xl shadow-cyan-500/25 text-sm px-6" data-testid="hero-book-btn">
                    <Link to="/book-now">Book Your Session <ArrowRight className="ml-2 h-4 w-4" /></Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 backdrop-blur-sm text-sm" data-testid="hero-membership-btn">
                    <Link to="/membership">View Memberships</Link>
                  </Button>
                </div>
              </div>
              <div className="mt-8 grid grid-cols-4 gap-3">
                {stats.map((stat, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/10 text-center">
                    <div className="text-xl lg:text-2xl font-bold text-cyan-400">{stat.number}</div>
                    <div className="text-xs text-gray-300 mt-0.5">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ----- MOBILE HERO (< md) ----- */}
        <div className="md:hidden">
          {/* Full-width video visible in landscape aspect */}
          <div className="relative w-full">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full bg-black"
              style={{ aspectRatio: '16/9' }}
              onCanPlayThrough={handleVideoReady}
              onPlaying={handleVideoReady}
            >
              <source src={HERO_VIDEO_URL} type="video/mp4" />
            </video>
            {/* Heated Pool Badge - Top Right on mobile video */}
            <div className="absolute top-2 right-2 z-[2]" data-testid="heated-pool-badge-mobile">
              <div className="flex items-center gap-1 bg-cyan-600/80 backdrop-blur-sm text-white px-2 py-1 rounded-full text-[10px] font-medium">
                <Thermometer className="h-3 w-3" />
                Heated Pool
              </div>
            </div>
          </div>

          {/* Text content below video */}
          <div className="px-4 py-6 bg-gradient-to-b from-gray-900 to-[#0a1628] space-y-4">
            <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-400/30 text-xs px-3 py-0.5">
              Bangalore's Only Non-Chlorine Pool
            </Badge>
            <h1 className="text-3xl font-bold text-white leading-tight tracking-tight">
              Swim Pure.{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-teal-300 bg-clip-text text-transparent">
                Swim Safe.
              </span>
            </h1>
            <p className="text-sm text-gray-300 leading-relaxed">
              India's most advanced non-chlorine pool powered by salt ionization & UV filtration.
            </p>
            <div className="flex flex-col gap-3 pt-1">
              <Button asChild size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white text-sm w-full" data-testid="hero-book-btn-mobile">
                <Link to="/book-now">Book Your Session <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 text-sm w-full" data-testid="hero-membership-btn-mobile">
                <Link to="/membership">View Memberships</Link>
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              {stats.map((stat, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-md rounded-xl p-2.5 border border-white/10 text-center">
                  <div className="text-lg font-bold text-cyan-400">{stat.number}</div>
                  <div className="text-xs text-gray-300">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== VIDEO / YOUTUBE PLACEHOLDERS ========== */}
      <section className="py-20 bg-[#0a1628]" data-testid="video-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">See Our Pool in Action</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Watch how our advanced non-chlorine pool delivers the ultimate swimming experience
            </p>
          </div>

          {/* Main Video - YouTube Embed */}
          <div className="relative aspect-video rounded-2xl overflow-hidden mb-8 border border-white/10">
            <iframe
              src="https://www.youtube.com/embed/UqBn45vrmr4?rel=0&modestbranding=1"
              title="Hurupu Pools"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              frameBorder="0"
            />
          </div>

          {/* Secondary Videos Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { img: images.kidsTraining, title: 'Kids Training Sessions' },
              { img: images.familyPool, title: 'Family Swimming' },
              { img: images.poolLounge, title: 'Premium Amenities' },
            ].map((vid, i) => (
              <div
                key={i}
                className="relative aspect-video rounded-xl overflow-hidden group cursor-pointer border border-white/10"
              >
                <img
                  src={vid.img}
                  alt={vid.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-cyan-500/80 transition-colors duration-300">
                    <Play className="h-7 w-7 text-white ml-0.5" fill="white" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                  <p className="text-white font-medium text-sm">{vid.title}</p>
                  <p className="text-gray-400 text-xs">YouTube Placeholder</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WHY CHOOSE HURUPU POOL ========== */}
      <section className="py-20 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-16">
            <Badge className="bg-cyan-50 text-cyan-700 border-cyan-200">What Sets Us Apart</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Why Choose Hurupu Pool
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              We combine innovation, safety, and premium design to deliver a healthier and more
              enjoyable swimming experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChoose.map((item, i) => {
              const Icon = iconMap[item.icon] || CheckCircle;
              return (
                <Card
                  key={item.id}
                  className="group border border-gray-100 hover:border-cyan-200 hover:shadow-xl hover:shadow-cyan-50 transition-all duration-300"
                  data-testid={`why-choose-${item.id}`}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-50 to-teal-50 flex items-center justify-center group-hover:from-cyan-100 group-hover:to-teal-100 transition-colors">
                      <Icon className="h-6 w-6 text-cyan-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== SERVICES SHOWCASE WITH IMAGES ========== */}
      <section className="py-20 bg-gray-50" data-testid="services-showcase">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Our Services</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              From self-swimming to professional training, we have something for everyone
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                img: images.poolIndoor,
                title: 'Self Swimming',
                desc: 'Individual & family plans with flexible hour-based recharge options. Enjoy our non-chlorine pool at your own pace.',
                link: '/membership',
                linkText: 'View Plans',
                color: 'from-cyan-600 to-blue-600',
              },
              {
                img: images.kidsTraining,
                title: 'Kids & Adults Training',
                desc: 'Batch training sessions for all skill levels. From 16 to 100 sessions with sharing options.',
                link: '/training',
                linkText: 'See Programs',
                color: 'from-teal-600 to-cyan-600',
              },
              {
                img: images.babySwim,
                title: 'Infant Swimming',
                desc: 'Specially designed programs for infants with certified instructors. Safe and nurturing environment.',
                link: '/training',
                linkText: 'Learn More',
                color: 'from-pink-500 to-rose-500',
              },
              {
                img: images.jacuzzi,
                title: 'Jacuzzi & Water Therapy',
                desc: 'Premium relaxation and therapeutic water sessions for recovery, wellness, and stress relief.',
                link: '/book-now',
                linkText: 'Book Now',
                color: 'from-purple-600 to-indigo-600',
              },
            ].map((service, i) => (
              <Card
                key={i}
                className="overflow-hidden border-0 shadow-lg group hover:shadow-xl transition-all duration-300"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.img}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <h3 className="absolute bottom-4 left-5 text-xl font-bold text-white">
                    {service.title}
                  </h3>
                </div>
                <CardContent className="p-5 space-y-4">
                  <p className="text-gray-600 text-sm leading-relaxed">{service.desc}</p>
                  <Button
                    asChild
                    className={`bg-gradient-to-r ${service.color} hover:opacity-90`}
                    size="sm"
                  >
                    <Link to={service.link}>
                      {service.linkText}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FEATURED ON PLAYO & DISTRICT PLAY ========== */}
      <section className="py-16 bg-white border-y border-gray-100" data-testid="featured-on-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-8">
            Find Us On
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16">
            {/* Playo */}
            <a
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 group"
              data-testid="playo-link"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                <span className="text-white font-black text-2xl">P</span>
              </div>
              <div className="text-left">
                <p className="font-bold text-gray-900 text-lg group-hover:text-green-600 transition-colors">
                  Playo
                </p>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  Book on Playo <ExternalLink className="h-3 w-3" />
                </p>
              </div>
            </a>

            <div className="hidden sm:block w-px h-12 bg-gray-200" />

            {/* District Play */}
            <a
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 group"
              data-testid="district-play-link"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                <span className="text-white font-black text-2xl">D</span>
              </div>
              <div className="text-left">
                <p className="font-bold text-gray-900 text-lg group-hover:text-orange-600 transition-colors">
                  District Play
                </p>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  Book on District Play <ExternalLink className="h-3 w-3" />
                </p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ========== CTA SECTION ========== */}
      <section className="relative py-24 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img
            src={images.poolLounge}
            alt="Pool"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/90 to-teal-900/80" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 space-y-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Ready to Make a Splash?</h2>
          <p className="text-lg text-cyan-100 max-w-xl mx-auto">
            Join Hurupu Pools today and experience Bangalore's most advanced non-chlorine swimming pool
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-white text-cyan-800 hover:bg-gray-50 shadow-xl"
            >
              <Link to="/membership">
                <Star className="mr-2 h-5 w-5" />
                Get Membership
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-white/40 text-white hover:bg-white/10"
            >
              <Link to="/contact">
                <Users className="mr-2 h-5 w-5" />
                Contact Us
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
